import PageContainer from 'components/PageContainer/PageContainer';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import RespuestaServidor from './RespuestaServidor';
import { FavoriteBorder } from '@mui/icons-material';
import PasosDeCompra from 'components/public/FichaEspectaculo/PasosDeCompra';
import { pollCompraStatus } from 'services/paymentStatus';
import Spinner from 'components/public/Spinner/Spinner';

const PAYMENT_FLOW_MODE_KEY = 'paymentFlowMode';
const ADMIN_OTHER_CHANNEL_FLOW_MODE = 'admin-other-channel';

const SuccessCompra = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [paymentStatus, setPaymentStatus] = useState<'validating' | 'approved' | 'timeout'>('validating');
  const [showTimeoutMessage, setShowTimeoutMessage] = useState(false);

  // Obtener ID de compra desde localStorage
  const idCompra = localStorage.getItem('idCompra');

  useEffect(() => {
    if (!idCompra) {
      navigate('/payment/failure');
      return;
    }

    const params = new URLSearchParams(location.search);
    const mpStatus = params.get('status') || params.get('collection_status');
    if (mpStatus === 'approved') {
      setPaymentStatus('approved');
      return;
    }

    const paymentFlowMode = localStorage.getItem(PAYMENT_FLOW_MODE_KEY);
    if (paymentFlowMode === 'test' || paymentFlowMode === ADMIN_OTHER_CHANNEL_FLOW_MODE) {
      setPaymentStatus('approved');
      return;
    }

    const poll = pollCompraStatus(
      Number(idCompra),
      (isApproved: boolean) => {
        if (isApproved) {
          setPaymentStatus('approved');
        }
      },
      20,
      3000
    );

    poll.result.then(approved => {
      if (!approved) {
        setPaymentStatus('timeout');
        setShowTimeoutMessage(true);
      }
    });

    return () => poll.cancel();
  }, [idCompra, location.search, navigate]);

  useEffect(() => {
    // Limpiar localStorage después de mostrar éxito
    if (paymentStatus === 'approved') {
      const timer = setTimeout(() => {
        localStorage.removeItem('datosCompra');
        localStorage.removeItem('idCompra');
        localStorage.removeItem('expiracionCompra');
        localStorage.removeItem(PAYMENT_FLOW_MODE_KEY);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [paymentStatus]);

  useEffect(() => {
    // Redirigir después de 10 segundos si el pago fue aprobado
    if (paymentStatus === 'approved') {
      const timer = setTimeout(() => {
        navigate('/');
      }, 10000);
      
      return () => clearTimeout(timer);
    }
  }, [paymentStatus, navigate]);

  const getDisplayContent = () => {
    switch (paymentStatus) {
      case 'validating':
        return {
          titulo: 'Validando tu pago...',
          texto: 'Estamos procesando tu pago. Por favor, espera un momento.',
          showSpinner: true
        };
      case 'approved':
        if (localStorage.getItem(PAYMENT_FLOW_MODE_KEY) === ADMIN_OTHER_CHANNEL_FLOW_MODE) {
          return {
            titulo: '¡La compra se registró con éxito!',
            texto: 'Las entradas quedaron generadas y serás redirigido/a a la página de inicio.',
            showSpinner: false
          };
        }

        return {
          titulo: '¡Tu pago se ha acreditado con éxito!',
          texto: (
            <>
              Recibirás un correo con tu/s entrada/s y serás redirigido/a a la página de inicio.
              <br />
              <strong>Si no te llegó el correo, fijate en la carpeta de spam.</strong>
            </>
          ),
          showSpinner: false
        };
      case 'timeout':
        return {
          titulo: 'Estamos procesando tu pago',
          texto: showTimeoutMessage 
            ? 'Si ya realizaste el pago, recibirás tus entradas por correo electrónico en breve.'
            : 'Validando tu pago...',
          showSpinner: false
        };
      default:
        return {
          titulo: 'Validando tu pago...',
          texto: 'Estamos procesando tu pago.',
          showSpinner: true
        };
    }
  };

  const content = getDisplayContent();

  return (
    <PageContainer marginBottom={true}>
      {paymentStatus === 'validating' || (paymentStatus === 'approved' && !showTimeoutMessage) ? (
        <>
          <PasosDeCompra paso={3} />
          {content.showSpinner ? (
            <Spinner />
          ) : (
            <RespuestaServidor
              titulo={content.titulo}
              texto={content.texto}
              icono={FavoriteBorder}
              textoBoton="Volver"
              colorIcono="success"
              paginaANavegar={'/'}
            />
          )}
        </>
      ) : (
        <RespuestaServidor
          titulo={content.titulo}
          texto={content.texto}
          icono={FavoriteBorder}
          textoBoton="Ir a Mis Compras"
          colorIcono={paymentStatus === 'approved' ? 'success' : 'info'}
          paginaANavegar={'/usuario/miscompras'}
        />
      )}
    </PageContainer>
  );
};

export default SuccessCompra;
